import LogViewer from '../logViewer/LogViewer.tsx';
import LogLevelFilter from '../logViewer/LogLevelFilter.tsx';
import { Dialog, DialogProps, Paper, useMediaQuery, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { FaDownload } from 'react-icons/fa6';
import CloseButton from 'components/button/CloseButton';
import { iconButtonPillStyle } from 'components/button/iconButtonPillStyle';
import { useQuery } from '@tanstack/react-query';
import { downloadNodeLogsFile } from '../../api/workflows/requests.ts';
import Loader from '../Loader.tsx';
import { downloadBlob } from '../../utils/helpers.tsx';
import { toast } from 'react-toastify';
import { MouseEvent, useEffect } from 'react';
import { ExecutionNodeStatusEnum } from 'types.ts';
import { useNodeLogsStream } from '../../api/sse/useNodeLogsStream';
import { QueryKey } from 'api/model.ts';

type LogViewerDialogsProps = {
  open: boolean;
  onClose: () => void;
  executionId: string;
  nodeId: string;
  filename: string;
  nodeStatus?: ExecutionNodeStatusEnum;
} & DialogProps;

type QueryData = {
  blob: Blob;
  content: string;
};

const LogViewerDialog = ({ open, onClose, executionId, nodeId, filename, nodeStatus }: LogViewerDialogsProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data: { blob, content } = {},
    isLoading,
    error,
    isError,
    refetch,
  } = useQuery<QueryData, Error>({
    queryKey: [QueryKey.WorkflowExecutionNodeLogs, executionId, nodeId, nodeStatus],
    queryFn: async (): Promise<QueryData> => {
      const blob = await downloadNodeLogsFile(executionId, nodeId);
      const content = await blob.text();
      return { blob, content };
    },
    enabled: open,
  });

  useNodeLogsStream(executionId, nodeId, open && nodeStatus === ExecutionNodeStatusEnum.RUNNING, {
    onUpdate: () => {
      void refetch();
    },
  });

  useEffect(() => {
    if (open && isError) {
      toast.error(error?.message ?? 'Error when fetching logs');
      onClose();
    }
  }, [isError, open, onClose, error]);

  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    onClose();
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth={!isLoading}
      open={open}
      PaperComponent={Paper}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
    >
      {!isLoading ? (
        <>
          <div className="flex justify-between px-3 py-2">
            <IconButton
              aria-label="download logs"
              onClick={() => downloadBlob(blob!, `${filename}.log`)}
              sx={iconButtonPillStyle}
            >
              <FaDownload />
            </IconButton>
            <LogLevelFilter />
            <CloseButton onClose={onClose} />
          </div>
          <LogViewer logs={content ?? ''} status={nodeStatus} />
        </>
      ) : (
        <div className="p-4">
          Loading logs
          <Loader />
        </div>
      )}
    </Dialog>
  );
};
export default LogViewerDialog;
